import React, { useMemo, useState } from 'react'
import SaveFixedButton from '../shared/SaveFixedButton'
import FormInput from '../shared/FormInput'
import CreateButton from '../shared/CreateButton'
import SubmitButton from '../shared/SubmitButton'
import EntityLoader from '../shared/EntityLoader'

export default function EditApiIntegration({ appProps, apiIntegration, apiIntegrationEditPath, updatePath, migrateFrom, newMigrationPath, migrationsPath }) {
  const formatSettings = settings => settings.map(setting => ['true', 'false'].includes(setting.value) ? { ...setting, value: setting.value === 'true' } : setting)
  const isMigration = !!migrateFrom

  const [settings, setSettings]           = useState(formatSettings(apiIntegration.settings))
  const [saveButtonRef, setSaveButtonRef] = useState(null)
  const [loading, setLoading]             = useState(false)

  const migrationTitle = useMemo(() => {
    if (!isMigration) return null

    return Website.translations.migrateFromTo
      .replace('%{api_title_from}', migrateFrom.replace('_', ' '))
      .replace('%{api_title_to}', apiIntegration.apiTitle.replace('_', ' '))
  }, [isMigration, migrateFrom, apiIntegration.apiTitle])

  const handleChange = (e, key) => {
    !isMigration && saveButtonRef.setSaveableStatus()
    setSettings(prevState => {
      const index = settings.findIndex(setting => setting.key === key)
      return [
        ...prevState.slice(0, index),
        { ...prevState[index], value: e.target.value },
        ...prevState.slice(index + 1)
      ]
    })
  }

  const save = () => {
    const fd = new FormData()
    settings.forEach(setting => {
      fd.append(`settings[${setting.key}]`, setting.value)
    })

    saveButtonRef.setSavingStatus()

    Rails.ajax({
      type: 'PATCH',
      url: updatePath,
      dataType: 'json',
      data: fd,
      success: res => {
        saveButtonRef.setSuccessStatus()
        setSettings(formatSettings(res.settings))
      },
      error: res => {
        saveButtonRef.setErrorStatusWithMessage(res.error)
      }
    })
  }

  const renderSetting = (setting, index) => {
    let { key, value } = setting
    let label = key.replaceAll('_', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    if (label.startsWith('Invoicing Software ')) {
      label = label.replace('Invoicing Software ', '')
    }
    let type = ['password', 'invoicing_software_api_key', 'client_secret'].includes(key) ? 'password' : null
    if (['invoicing_software_production_mode', 'production_mode'].includes(key)) {
      type = 'toggle'
    }

    let onChange = type === 'toggle' ? _e => prev: e => handleChange(e, key)
    if (type === 'toggle') {
      onChange = _e => {
        !isMigration && saveButtonRef.setSaveableStatus()
        setSettings(prevState => {
          const index = settings.findIndex(setting => setting.key === key)
          return [
            ...prevState.slice(0, index),
            { ...prevState[index], value: !prevState[index].value },
            ...prevState.slice(index + 1)
          ]
        })
      }
    } else {
      onChange = e => handleChange(e, key)
    }

    return (
      <FormInput
        key={ index }
        label={ label }
        type={ type }
        value={ value || '' }
        onChange={ onChange }
      />
    )
  }

  const migrate = () => {
    if (!isMigration) return false

    setLoading(true)

    const fd = new FormData()
    settings.forEach(setting => {
      fd.append(`settings[${setting.key}]`, setting.value)
    })

    Rails.ajax({
      type: 'POST',
      url: migrationsPath,
      dataType: 'json',
      data: fd,
      success: res => {
        saveButtonRef.setSuccessStatus()
        setTimeout(() => {
          location.href = res.location
        }, 1500)
      },
      error: res => {
        saveButtonRef.setErrorStatusWithMessage(res.error)
        setLoading(false)
      },
    })
  }

  return (
    <>
      <CreateButton
        pageTitle={ isMigration ? migrationTitle : apiIntegration.apiTitle }
        breadcrumbs={ appProps.breadcrumbs }
      />

      <div className='card'>
        <div className="card-header border-0">
          <div className="card-title m-0">
            { Website.translations.settings }
          </div>
        </div>
        <div className='card-body'>
          { settings.map(renderSetting) }

          { !isMigration && apiIntegration.allowedMigrationTargets?.length > 0 &&
            <div className='mt-10'>
              { apiIntegration.allowedMigrationTargets.map(target => (
                <a key={ target } href={ `${newMigrationPath}?to=${target}` } className="text-hover-primary">
                  { Website.translations.migrateTo.replace('%{api_title}', target.replace('_', ' ')) }
                </a>
              )) }
            </div>
          }
        </div>

        { isMigration &&
          <div className='flex-box content-end items-center gap-6'>
            <a href={ apiIntegrationEditPath } className="text-muted text-hover-primary" >
              { Website.translations.cancel }
            </a>

            <SubmitButton
              value={ Website.translations.migrateTo.replace('%{api_title}', apiIntegration.apiTitle.replace('_', ' ')) }
              className='btn btn-primary mw-200px'
              onSubmit={ migrate }
            />
          </div>
        }
      </div>

      { loading && <EntityLoader fullPage /> }

      <SaveFixedButton
        onSave={ save }
        entity={ Website.translations.apiIntegration }
        ref={ ref => setSaveButtonRef(ref) }
      />
    </>
  )
}
